import { runtimeEnv } from 'config/runtimeEnv';
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Box, Grid, Typography, IconButton, Tooltip, Modal } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import ArrowBackIosRoundedIcon from '@mui/icons-material/ArrowBackIosRounded';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  getReservationDetails,
  markTripAsNoShow,
  cancelPaymentLink,
} from 'redux/actions/reservationAction';
import CommonButton from 'components/commonButton/CommonButton';
import Loader from 'components/loader/Loader';
import requestInstance from 'services/Request';
import { API_ENDPOINT } from 'services/Api';
import { getTollChargesService } from 'services/Reservation';

import { formatCurrency, getDateFormat, getReservationDetailsObject } from 'utils/utils';
import useModal from 'hooks/useModal';
import {
  bookingDetailsData,
  bookingTime,
  fareBreakupData,
} from '../bookingDetails/BookingDetailsData';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ReservationAttachmentsDialog from '../bookingDetails/ReservationAttachmentsDialog';
import DutySlipDialog from '../bookingDetails/DutySlipDialog';

const PAYMENT_LINK_STATUS_CONFIG = {
  active: {
    backgroundColor: '#e6f9ef',
    color: '#1a8a4a',
    border: '#a8dfbf',
    label: 'Active',
    icon: '●',
  },
  cancelled: {
    backgroundColor: '#fdecea',
    color: '#c62828',
    border: '#f5a8a0',
    label: 'Cancelled',
    icon: '✕',
  },
  expired: {
    backgroundColor: '#fff3e0',
    color: '#e65100',
    border: '#ffcc80',
    label: 'Expired',
    icon: '⚠',
  },
  used: {
    backgroundColor: '#e3f2fd',
    color: '#1565c0',
    border: '#90caf9',
    label: 'Used',
    icon: '✓',
  },
};

const ManageInvoiceBookingDetails = () => {
  const { open: attachmentsModal, toggle: setAttachmentsModal } = useModal();
  const { open: editChargesModal, toggle: setEditChargesModal } = useModal();
  const { open: paymentLinkModal, toggle: setPaymentLinkModal } = useModal();
  const { open: fareBreakupModal, toggle: setFareBreakupModal } = useModal();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const startDate = searchParams.get('startDate');
  const endDate = searchParams.get('endDate');

  const tripDetails = useSelector((state) => state.getReservationDetails?.reservationDetails?.data);
  const reservationDataLoading = useSelector((state) => state.getReservationDetails?.loading);
  const noShowLoading = useSelector((state) => state?.markTripNoShow?.loading);
  const cancelPaymentLinkLoading = useSelector((state) => state?.cancelPaymentLink?.loading);

  const tripStatus = tripDetails?.status?.toLowerCase();
  const hasInvoice = tripDetails?.Invoices?.length > 0;
  const isBillToCard = tripDetails?.additionalInfo?.billingType == 1;
  const showPaymentLink = hasInvoice && isBillToCard;

  // Check if payment is already completed
  const isPaymentCompleted = React.useMemo(() => {
    const paymentStatus = tripDetails?.Payments?.[0]?.status?.toLowerCase();
    return paymentStatus === 'success' || paymentStatus === 'paid';
  }, [tripDetails?.Payments]);

  const [paymentLink, setPaymentLink] = useState('');
  const [paymentLinkStatus, setPaymentLinkStatus] = useState('');
  const [paymentLinkLoading, setPaymentLinkLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [paymentLinkExpiry, setPaymentLinkExpiry] = useState(null);
  const [paymentLinkCreatedAt, setPaymentLinkCreatedAt] = useState(null);
  const [cancelConfirmModal, setCancelConfirmModal] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');

  const details = getReservationDetailsObject(tripDetails);
  const [tollFilesCount, setTollFilesCount] = useState(0);
  const [parkingFilesCount, setParkingFilesCount] = useState(0);
  const [noShowModal, setNoShowModal] = useState(false);
  const [noShowRemarks, setNoShowRemarks] = useState('');

  const env = runtimeEnv('B2B_API_BASE_URL');

  // Check if payment link is actually active (not expired)
  const isPaymentLinkActive = useCallback(() => {
    if (!paymentLinkStatus) return false;

    const statusLower = paymentLinkStatus.toLowerCase();
    if (statusLower !== 'active') return false;

    // Check if link is expired based on expiry date
    if (paymentLinkExpiry) {
      const now = new Date();
      const expiryDate = new Date(paymentLinkExpiry);
      if (now > expiryDate) {
        setPaymentLinkStatus('Expired');
        return false;
      }
    }

    return true;
  }, [paymentLinkStatus, paymentLinkExpiry]);

  const fetchPaymentLink = useCallback(async () => {
    if (!id) return;

    // Don't fetch if payment is already completed
    if (isPaymentCompleted) {
      setPaymentLink('');
      setPaymentLinkStatus('Cancelled');
      return;
    }

    try {
      setPaymentLinkLoading(true);
      const response = await requestInstance.get(
        `${env}${API_ENDPOINT.reservation.retrievePaymentLinkApi}/${id}`,
      );
      const linkData = response?.data?.data;
      setPaymentLink(linkData?.paymentLink || '');
      setPaymentLinkStatus(linkData?.paymentLinkStatus || '');
      setPaymentLinkExpiry(linkData?.expiryTime || null);
      setPaymentLinkCreatedAt(linkData?.createdAt || null);

      // Auto-update status if expired
      if (linkData?.expiryTime && linkData.paymentLinkStatus === 'Active') {
        const now = new Date();
        const expiryDate = new Date(linkData.expiryTime);
        if (now > expiryDate) {
          setPaymentLinkStatus('Expired');
        }
      }
    } catch (err) {
      setPaymentLink('');
      setPaymentLinkStatus('');
      setPaymentLinkExpiry(null);
      setPaymentLinkCreatedAt(null);
    } finally {
      setPaymentLinkLoading(false);
    }
  }, [id, isPaymentCompleted, env]);

  // Auto-cancel payment link when payment is marked as completed
  useEffect(() => {
    if (isPaymentCompleted && paymentLinkStatus === 'Active') {
      setPaymentLinkStatus('Cancelled');
      setPaymentLink('');
      toast.info('Payment link auto-cancelled as payment is already completed');
    }
  }, [isPaymentCompleted, paymentLinkStatus]);

  useEffect(() => {
    if (id) {
      dispatch(getReservationDetails(id));
    }
  }, [id, dispatch]);

  const unwrapTollBody = (res) => {
    let data = res?.data;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.body)) return data.body;
    if (Array.isArray(data?.data?.body)) return data.data.body;
    if (Array.isArray(data?.list)) return data.list;
    return [];
  };

  const fetchTollParkingCounts = useCallback(async () => {
    const tripIdVal = tripDetails?.id;
    if (!tripIdVal) return;
    try {
      const [tollRes, parkingRes] = await Promise.all([
        getTollChargesService({
          tripId: tripIdVal,
          type: 'Toll',
          page: 1,
          pageSize: 50,
        }),
        getTollChargesService({
          tripId: tripIdVal,
          type: 'Parking',
          page: 1,
          pageSize: 50,
        }),
      ]);
      const tollBody = unwrapTollBody(tollRes);
      const parkingBody = unwrapTollBody(parkingRes);
      setTollFilesCount(
        tollBody.reduce((sum, c) => sum + (c?.TollAndParkingReceipts?.length || 0), 0),
      );
      setParkingFilesCount(
        parkingBody.reduce((sum, c) => sum + (c?.TollAndParkingReceipts?.length || 0), 0),
      );
    } catch (error) {
      setTollFilesCount(0);
      setParkingFilesCount(0);
    }
  }, [tripDetails?.id]);

  useEffect(() => {
    fetchTollParkingCounts();
  }, [fetchTollParkingCounts]);

  const handleCopyLink = () => {
    if (!isPaymentLinkActive()) {
      toast.error('Cannot copy - Payment link is not active');
      return;
    }
    navigator.clipboard.writeText(paymentLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRegenerateLink = async () => {
    if (isPaymentCompleted) {
      toast.error('Cannot regenerate link - Payment already completed');
      return;
    }

    try {
      setPaymentLinkLoading(true);
      const response = await requestInstance.get(
        `${env}${API_ENDPOINT.reservation.generatePaymentLinkApi}/${id}`,
      );
      const linkData = response?.data?.data;
      setPaymentLink(linkData?.paymentLink || '');
      setPaymentLinkStatus('Active');
      setPaymentLinkExpiry(linkData?.expiryTime || null);
      setPaymentLinkCreatedAt(linkData?.createdAt || null);

      toast.success('Payment link regenerated successfully');
    } catch (err) {
      toast.error('Failed to regenerate payment link');
      setPaymentLink('');
    } finally {
      setPaymentLinkLoading(false);
    }
  };

  const handleCancelPaymentLinkClick = () => {
    if (!isPaymentLinkActive()) {
      toast.error('Cannot cancel - Link is not active');
      return;
    }
    setCancelConfirmModal(true);
    setCancellationReason('');
  };

  const handleConfirmCancelPaymentLink = () => {
    dispatch(
      cancelPaymentLink({ tripId: id, reason: cancellationReason }, () => {
        setPaymentLinkStatus('Cancelled');
        setPaymentLink('');
        setCancelConfirmModal(false);
        setCancellationReason('');
      }),
    );
  };

  const filterValue = (key) => {
    const keys = [
      'Reporting Address',
      'Drop Address',
      'Via Address',
      'Landmark',
      'Special Instruction',
      'Trip Charges',
      'Flight No',
      'Cancellation Reason',
    ];

    if (key === 'Special Instruction' && tripDetails?.DriverProfile) {
      return '';
    } else if (keys.includes(key)) {
      return 'expandRow';
    }
  };

  const handleBackBtn = () => {
    navigate('/manage-invoice');
  };

  const handleMarkNoShow = () => {
    if (!noShowRemarks.trim()) {
      toast.error('Please enter remarks for the no-show');
      return;
    }

    dispatch(markTripAsNoShow({ tripId: tripDetails?.id, remarks: noShowRemarks }, 'details'));

    setNoShowModal(false);
    setNoShowRemarks('');
  };

  const getStatusBadgeConfig = (status) => {
    return (
      PAYMENT_LINK_STATUS_CONFIG[status?.toLowerCase()] || PAYMENT_LINK_STATUS_CONFIG.cancelled
    );
  };

  const renderPaymentLinkContent = () => {
    if (paymentLinkLoading) {
      return (
        <Box className="justifyCenter" sx={{ minHeight: 100 }}>
          <Loader height="80px" />
        </Box>
      );
    }

    // Payment already completed
    if (isPaymentCompleted) {
      return (
        <Box>
          <Box
            className="mb20 p15"
            sx={{
              backgroundColor: '#e6f9ef',
              border: '1px solid #a8dfbf',
              borderRadius: 1.5,
            }}
          >
            <Typography variant="body2" className="colorSuccess fw500">
              ✓ Payment has been completed. Payment link operations are disabled.
            </Typography>
          </Box>
          {paymentLink && (
            <Box
              className="mt10 p10"
              sx={{
                backgroundColor: '#f5f5f5',
                borderRadius: 1,
              }}
            >
              <Typography variant="body3" className="colorGrey">
                Original link (cancelled): {paymentLink}
              </Typography>
            </Box>
          )}
        </Box>
      );
    }

    // Active link
    if (paymentLink && isPaymentLinkActive()) {
      return (
        <Box>
          {paymentLinkExpiry && (
            <Typography variant="body3" className="colorGrey mb10">
              Link expires: {getDateFormat(paymentLinkExpiry, 'dateTimeFormat')}
            </Typography>
          )}
          {paymentLinkCreatedAt && (
            <Typography variant="body3" className="colorGrey mb15">
              Created: {getDateFormat(paymentLinkCreatedAt, 'dateTimeFormat')}
            </Typography>
          )}
          <Grid container alignItems="center" gap={1} flexWrap="nowrap">
            <Grid item xs>
              <Box
                className="p10"
                sx={{
                  backgroundColor: '#f5f5f5',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  wordBreak: 'break-all',
                }}
              >
                <Typography variant="body2" className="colorPrimary">
                  {paymentLink}
                </Typography>
              </Box>
            </Grid>
            <Grid item>
              <Tooltip title={copied ? 'Copied!' : 'Copy Link'}>
                <IconButton onClick={handleCopyLink} size="small" color="primary">
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
          <Box className="mt20" sx={{ display: 'flex', gap: 1.5 }}>
            <CommonButton name="Regenerate" btnClass="btnSm" onClick={handleRegenerateLink} />
            <CommonButton
              name="Cancel Link"
              btnClass="btnSm btnDanger"
              onClick={handleCancelPaymentLinkClick}
              disabled={cancelPaymentLinkLoading}
            />
          </Box>
        </Box>
      );
    }

    // Cancelled or Expired link
    if (
      paymentLinkStatus?.toLowerCase() === 'cancelled' ||
      paymentLinkStatus?.toLowerCase() === 'expired'
    ) {
      const isExpired = paymentLinkStatus?.toLowerCase() === 'expired';
      return (
        <Box>
          <Box
            className="p20 mb20"
            sx={{
              backgroundColor: isExpired ? '#fff3e0' : '#fdecea',
              border: `1px solid ${isExpired ? '#ffcc80' : '#f5a8a0'}`,
              borderRadius: 1.5,
            }}
          >
            <Typography
              variant="body2"
              className="fw500"
              sx={{ color: isExpired ? '#e65100' : '#c62828' }}
            >
              {isExpired
                ? '⚠ This payment link has expired'
                : '✕ This payment link has been cancelled'}
            </Typography>
            {paymentLink && (
              <Typography variant="body3" className="colorGrey mt10">
                Original link: {paymentLink}
              </Typography>
            )}
          </Box>
          <CommonButton name="Generate New Link" btnClass="btnSm" onClick={handleRegenerateLink} />
        </Box>
      );
    }

    // No link generated yet
    return (
      <Box>
        <Typography variant="body2" className="colorGrey mb20">
          No payment link has been generated yet.
        </Typography>
        <CommonButton
          name="Generate Payment Link"
          btnClass="btnSm"
          onClick={handleRegenerateLink}
        />
      </Box>
    );
  };

  return (
    <>
      {reservationDataLoading ? (
        <Loader />
      ) : (
        <Box>
          <Grid container className="pb20 justifyBetween">
            <Box className="flex" alignItems={'center'}>
              <ArrowBackIosRoundedIcon
                className="font24 colorGrey"
                role="presentation"
                onClick={() => handleBackBtn()}
              />
              <Typography variant={'head4'} className="colorPrimary pl10">
                Booking ID {tripDetails?.tripId}
              </Typography>
            </Box>
            {(tripStatus === 'completed' ||
              tripStatus === 'dispatched' ||
              tripStatus === 'ongoing') && (
              <Box className="flex" gap={2}>
                <CommonButton
                  name="Edit Duty Slip"
                  iconType="edit"
                  iconPlace="front"
                  btnClass="btnSm"
                  // Once the bill-to-self amount is paid (QR / payment link /
                  // marked paid), the duty slip must not be editable.
                  disabled={hasInvoice || isPaymentCompleted}
                  onClick={() => setEditChargesModal()}
                />
                {/* <CommonButton
                  name="Mark No-Show"
                  iconType="block"
                  iconPlace="front"
                  btnClass="btnSm btnDanger"
                  disabled={hasInvoice}
                  onClick={() => setNoShowModal(true)}
                /> */}
                {tripStatus === 'completed' && (
                  <CommonButton
                    name="Generate Invoice"
                    iconType="generate invoice"
                    iconPlace="front"
                    btnClass="btnSm"
                    disabled={hasInvoice}
                    onClick={() =>
                      navigate(
                        `/operation/billing/duty-slip?tripId=${tripDetails?.id}&companyId=${tripDetails?.companyId}&companyName=${tripDetails?.companyName}&cityId=${tripDetails?.city}&cityName=${tripDetails?.location}&status=pendingApproval&pageType=manageInvoice&startDate=${startDate}&endDate=${endDate}&page=1&rowPerPage=10&search=`,
                      )
                    }
                  />
                )}
                {showPaymentLink && (
                  <CommonButton
                    name="Payment Link"
                    iconPlace="front"
                    btnClass="btnSm"
                    disabled={isPaymentCompleted}
                    onClick={() => {
                      fetchPaymentLink();
                      setPaymentLinkModal();
                    }}
                  />
                )}
              </Box>
            )}
          </Grid>
          <Box className="dashedBorderBottom"></Box>

          <Grid container justifyContent={'center'}>
            <Grid item xs={11}>
              <Grid container className="ptb30 justifyBetween">
                <Typography variant="head5" className="fw600 colorBlack">
                  Booking Details&nbsp;
                  <span className="font14 fw400">
                    ({getDateFormat(tripDetails?.updatedAt, 'fromNowFormat')})
                  </span>
                </Typography>

                {/* QA #56: restore the View Fare Breakup info button (present on main, dropped in the iisc-dev/MT lineage) */}
                {tripStatus === 'completed' && (
                  <Box className="flex" alignItems="center" gap={1}>
                    <Typography variant={'head5'} className="colorPrimary fw600">
                      Total Trip Charges - ₹&nbsp;
                      {formatCurrency(tripDetails?.finalPrice)}
                    </Typography>
                    <Tooltip title="View Fare Breakup">
                      <IconButton size="small" onClick={setFareBreakupModal}>
                        <InfoOutlinedIcon sx={{ fontSize: 20, color: '#000' }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Grid>

              {/* Payment Completed Banner */}
              {isPaymentCompleted && (
                <Box
                  className="mb20 p15"
                  sx={{
                    // backgroundColor: "#e6f9ef",
                    // border: "1px solid #282828",
                    // borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontWeight: '800',
                  }}
                >
                  <Typography variant="head5" className="fw600 colorBlack">
                    Payment Completed - Any active payment links have been automatically cancelled
                  </Typography>
                </Box>
              )}

              <Grid container className="ptb20 mb20 bgLightOrange">
                {bookingTime &&
                  bookingTime(tripDetails).map((data, index) => (
                    <Grid
                      key={index}
                      item
                      xs={12}
                      sm={3}
                      sx={{
                        padding: { xs: '20px 0px 0px 20px', sm: '0px' },
                        justifyContent: { xs: 'flex-start', sm: 'center' },
                      }}
                      container
                      className="justifyCenter"
                    >
                      <Box className="flex">
                        <img src={data.img} alt="Logo" />
                      </Box>
                      <Typography variant={'body2'} className="colorBlack pl20">
                        {data.title || 'NA'}
                      </Typography>
                    </Grid>
                  ))}
              </Grid>

              {/* No-Show Details Section */}
              {/* {tripDetails?.noShowDetails && (
                <Grid className="mb30 mt20" container>
                  <Grid item xs={12}>
                    <Box
                      className="p20 bgLightRed"
                      sx={{
                        borderRadius: 2,
                        border: "1px solid #ffcccc",
                      }}
                    >
                      <Typography
                        variant="head6"
                        className="fw600 colorError mb10"
                      >
                        No-Show Details
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body3" className="colorGrey">
                            Source
                          </Typography>
                          <Typography variant="body2" className="fw600">
                            {tripDetails.noShowDetails.source === "driver"
                              ? "Driver App"
                              : "Ops Portal"}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body3" className="colorGrey">
                            Pricing Mode
                          </Typography>
                          <Typography variant="body2" className="fw600">
                            {tripDetails.noShowDetails.pricingMode ===
                            "fixedAmount"
                              ? "Fixed Amount"
                              : "Trip Fare"}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body3" className="colorGrey">
                            Applied Amount
                          </Typography>
                          <Typography
                            variant="body2"
                            className="fw600 colorPrimary"
                          >
                            ₹
                            {formatCurrency(
                              tripDetails.noShowDetails.appliedAmount,
                            )}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body3" className="colorGrey">
                            Marked At
                          </Typography>
                          <Typography variant="body2" className="fw600">
                            {getDateFormat(
                              tripDetails.noShowDetails.markedAt,
                              "dateTimeFormat",
                            )}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body3" className="colorGrey">
                            Remarks
                          </Typography>
                          <Typography variant="body2" className="fw600">
                            {tripDetails.noShowDetails.remarks || "N/A"}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                </Grid>
              )} */}

              <Grid container className="mt40 mb20">
                {bookingDetailsData(tripDetails, details) &&
                  bookingDetailsData(tripDetails, details).map((value, key) => (
                    <Grid
                      key={key}
                      item
                      xs={12}
                      md={filterValue(value?.title, value?.status) ? 12 : 6}
                      sx={{ display: value?.title ? 'flex' : 'none' }}
                      container
                    >
                      <Grid
                        xs={4}
                        md={filterValue(value?.title, value?.status) ? 2 : 4}
                        item
                        className="p20 alignStart gridBorder"
                      >
                        <Typography variant={'body2'} className="colorGrey">
                          {value.title || 'NA'}
                        </Typography>
                      </Grid>

                      <Grid
                        xs={8}
                        md={filterValue(value?.title, value?.status) ? 10 : 8}
                        item
                        flexWrap={'wrap'}
                        className="p20 alignStart gridBorder"
                      >
                        <Typography
                          variant={`${value?.textType === 'textBold' ? 'head5' : 'body2'}`}
                          className={`pr10 ${value?.textType === 'textBold' && 'fw700'}`}
                        >
                          {value.data || 'NA'}
                          {value.title === 'Duty Type' &&
                            tripDetails?.additionalInfo?.isMultiDay && (
                              <span className="font12 fontNormal colorGrey">{' (Multi-day)'}</span>
                            )}
                        </Typography>
                        {value?.inclusive && (
                          <Typography variant={'body3'} className={'colorGrey'}>
                            (Exclusive of GST and Toll & Parking)
                          </Typography>
                        )}
                        {value.title === 'Passenger Name' && value.isVip && (
                          <Typography variant={'body5'} className="vipBadge">
                            VIP
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                  ))}
              </Grid>

              {/* Toll & Parking Attachments Summary */}
              {tripStatus === 'completed' && (
                <Grid className="mb30 mt20" container>
                  <Grid item xs={12}>
                    <Grid container justifyContent="space-between" alignItems="center">
                      <Typography variant="head5" className="fw600 colorBlack">
                        Toll & Parking Attachments
                      </Typography>
                      <CommonButton
                        name="View / Manage Attachments"
                        iconType="view"
                        iconPlace="front"
                        btnClass="btnSm"
                        onClick={() => setAttachmentsModal()}
                      />
                    </Grid>
                    <Grid container className="mt10" alignItems="center" gap={3}>
                      <Typography variant="body2" className="colorGrey">
                        <AttachFileIcon className="font16" /> Tolls: {tollFilesCount} files
                      </Typography>
                      <Typography variant="body2" className="colorGrey">
                        <AttachFileIcon className="font16" /> Parking: {parkingFilesCount} files
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              )}

              {tripStatus === 'completed' && (
                <img
                  src={tripDetails?.tripRouteImage?.url}
                  alt="map"
                  className="width100 height400"
                />
              )}
            </Grid>
          </Grid>

          {/* No-Show Remarks Modal
          <Dialog
            open={noShowModal}
            onClose={() => setNoShowModal(false)}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle className="flex justifyBetween alignCenter">
              <Typography variant="head5" className="fw600 colorBlack">
                Mark as No-Show
              </Typography>
              <IconButton
                onClick={() => setNoShowModal(false)}
                aria-label="close"
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" className="colorGrey mb20">
                Are you sure you want to mark this trip as a No-Show? This
                action will update the trip status and apply the configured
                no-show charges.
              </Typography>
              <TextField
                label="Remarks *"
                fullWidth
                size="small"
                multiline
                rows={3}
                value={noShowRemarks}
                onChange={(e) => setNoShowRemarks(e.target.value)}
                placeholder="Enter reason for no-show (e.g., Passenger did not arrive at pickup point)"
              />
              <Grid
                container
                justifyContent="flex-end"
                className="mt20"
                spacing={2}
              >
                <Grid item>
                  <CommonButton
                    name="Cancel"
                    btnClass="btnSm btnSecondary"
                    onClick={() => setNoShowModal(false)}
                  />
                </Grid>
                <Grid item>
                  <CommonButton
                    name={noShowLoading ? "Saving..." : "Confirm No-Show"}
                    btnClass="btnSm"
                    onClick={handleMarkNoShow}
                    disabled={noShowLoading || !noShowRemarks.trim()}
                  />
                </Grid>
              </Grid>
            </DialogContent>
          </Dialog> */}

          <ReservationAttachmentsDialog
            open={attachmentsModal}
            handleClose={setAttachmentsModal}
            attachments={tripDetails?.additionalInfo?.attachments}
            tripId={tripDetails?.id}
            isInvoiceGenerated={hasInvoice}
            onSave={() => {
              dispatch(getReservationDetails(id));
              fetchTollParkingCounts();
            }}
          />

          <DutySlipDialog
            open={editChargesModal}
            handleClose={setEditChargesModal}
            tripId={tripDetails?.id}
            tripDetails={tripDetails}
          />

          {/* Fare Breakup Dialog (QA #56) */}
          <Modal
            open={fareBreakupModal || false}
            onClose={setFareBreakupModal}
            className="customModal"
          >
            <Box
              className="modalContent textLeft"
              sx={{ width: 500, maxHeight: '80vh', overflowY: 'auto', p: 3, borderRadius: 2 }}
            >
              <Grid container justifyContent="space-between" alignItems="center" className="pb10">
                <Typography variant="head4" className="colorPrimary fw600">
                  Fare Breakup
                </Typography>
                <CloseIcon className="cursorPointer" onClick={setFareBreakupModal} />
              </Grid>
              <Box sx={{ mt: 1 }}>
                {fareBreakupData(tripDetails).map((item, idx) => (
                  <Grid container key={idx} sx={{ py: 0.8 }}>
                    <Grid item xs={7}>
                      <Typography variant="body2" className="colorGrey">
                        {item.label}
                      </Typography>
                    </Grid>
                    <Grid item xs={5} sx={{ textAlign: 'right' }}>
                      <Typography variant="body2" className="fw500">
                        {item.isDiscount
                          ? `- ₹ ${formatCurrency(Math.abs(Number(item.value)))}`
                          : `₹ ${formatCurrency(Number(item.value))}`}
                      </Typography>
                    </Grid>
                  </Grid>
                ))}
              </Box>
            </Box>
          </Modal>

          {/* Payment Link Modal */}
          <Modal
            open={paymentLinkModal || false}
            onClose={setPaymentLinkModal}
            className="customModal"
          >
            <Box
              className="modalContent textLeft"
              sx={{
                width: 650,
                minHeight: 200,
                p: 3,
                borderRadius: 2,
              }}
            >
              <Grid container justifyContent="space-between" alignItems="center" className="pb20">
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                  }}
                >
                  <Typography variant="head4" className="colorPrimary fw600">
                    Payment Link
                  </Typography>
                  {paymentLinkStatus && (
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        px: 1.2,
                        py: 0.3,
                        borderRadius: '12px',
                        fontSize: '0.72rem',
                        fontWeight: 600,
                        backgroundColor: getStatusBadgeConfig(paymentLinkStatus).backgroundColor,
                        color: getStatusBadgeConfig(paymentLinkStatus).color,
                        border: `1px solid ${getStatusBadgeConfig(paymentLinkStatus).border}`,
                      }}
                    >
                      {getStatusBadgeConfig(paymentLinkStatus).icon}{' '}
                      {getStatusBadgeConfig(paymentLinkStatus).label}
                    </Box>
                  )}
                </Box>
                <CloseIcon className="cursorPointer" onClick={setPaymentLinkModal} />
              </Grid>

              {renderPaymentLinkContent()}
            </Box>
          </Modal>

          {/* Cancel Payment Link Confirmation Modal */}
          <Dialog
            open={cancelConfirmModal}
            onClose={() => setCancelConfirmModal(false)}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle className="flex justifyBetween alignCenter">
              <Typography variant="head5" className="fw600 colorBlack">
                Cancel Payment Link
              </Typography>
              <IconButton onClick={() => setCancelConfirmModal(false)} aria-label="close">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Box
                className="mb20 p15"
                sx={{
                  backgroundColor: '#fff3e0',
                  border: '1px solid #ffcc80',
                  borderRadius: 1.5,
                }}
              >
                <Typography variant="body2" className="fw500" sx={{ color: '#e65100' }}>
                  ⚠ Warning: This action cannot be undone. Customers will not be able to make
                  payments using this link.
                </Typography>
              </Box>
              <TextField
                label="Cancellation Reason"
                fullWidth
                size="small"
                multiline
                rows={2}
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                placeholder="Enter reason for cancelling the payment link (optional)"
              />
              <Grid container justifyContent="flex-end" className="mt20" spacing={2}>
                <Grid item>
                  <CommonButton
                    name="Back"
                    btnClass="btnSm btnSecondary"
                    onClick={() => setCancelConfirmModal(false)}
                  />
                </Grid>
                <Grid item>
                  <CommonButton
                    name={cancelPaymentLinkLoading ? 'Cancelling...' : 'Confirm Cancel'}
                    btnClass="btnSm btnDanger"
                    onClick={handleConfirmCancelPaymentLink}
                    disabled={cancelPaymentLinkLoading}
                  />
                </Grid>
              </Grid>
            </DialogContent>
          </Dialog>
        </Box>
      )}
    </>
  );
};

export default ManageInvoiceBookingDetails;
